import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';

// Booking medium values — mirror booking-service's BookingMedium enum
// (platform-services/booking-service .../create-booking-service.dto.ts).
export const BOOKING_MEDIUMS = ['IN_PERSON', 'VIDEO_CALL', 'PHONE', 'ANY'] as const;
export type BookingMedium = (typeof BOOKING_MEDIUMS)[number];

// Booking category values — mirror booking-service's BookingCategory enum.
export const BOOKING_CATEGORIES = [
  'STYLING',
  'HAIR',
  'BEAUTY',
  'EVENT_PLANNING',
  'INTERIOR',
  'CUSTOM_ORDER',
  'FOOD_TASTING',
  'GENERAL',
] as const;
export type BookingCategory = (typeof BOOKING_CATEGORIES)[number];

export interface BookingService {
  id: string;
  tenantId: string;
  solutionId: string;
  title: string;
  description: string | null;
  category: BookingCategory;
  durationMins: number;
  /** Fee in kobo (minor units). 0 when not charging. */
  fee: number;
  currency: string;
  /** The "charge customers for this booking" toggle. */
  chargingEnabled: boolean;
  medium: BookingMedium;
  maxBookingsPerSlot: number;
  bufferMins?: number;
  isActive: boolean;
  imageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBookingServicePayload {
  tenantId: string;
  solutionId: string;
  title: string;
  description?: string;
  category: BookingCategory;
  durationMins: number;
  /** Fee in kobo (minor units). */
  fee: number;
  currency?: string;
  chargingEnabled: boolean;
  medium?: BookingMedium;
  maxBookingsPerSlot?: number;
  /** Buffer time in minutes between bookings (booking-service `bufferMins`). */
  bufferMins?: number;
  isActive?: boolean;
}

export type UpdateBookingServicePayload = Partial<
  Omit<CreateBookingServicePayload, 'tenantId' | 'solutionId'>
>;

/**
 * Merchant-level booking rules (PE-2100). Mirrors osprey-api's GET/PATCH /bookings/settings
 * read model (booking-service). All fields are per-merchant (scoped by x-tenant-id).
 */
export interface BookingSettings {
  tenantId: string;
  /** Payment-hold TTL in seconds — how long a slot is held while checkout completes. */
  holdTtlSeconds: number;
  /** IANA timezone the schedule is evaluated in, e.g. "Africa/Lagos". */
  timezone: string;
  /** Minimum lead time (hours) before the earliest bookable slot. */
  minLeadHours: number;
  /** Notice a customer must give to cancel (hours). */
  cancellationNoticeHours: number;
  /** Notice a customer must give to reschedule (hours). */
  rescheduleNoticeHours: number;
  /**
   * Merchant-wide default gap (minutes) enforced between consecutive bookings
   * (booking-service `bufferMins`, settings scope). A service's own `bufferMins`
   * overrides this default when set (PE-2137).
   */
  defaultBufferMins?: number;
}

export type UpdateBookingSettingsPayload = Partial<Omit<BookingSettings, 'tenantId'>>;

export interface AvailableSlot {
  start: string;
  end: string;
  available: boolean;
}

export interface AvailableSlots {
  serviceId: string;
  slots: AvailableSlot[];
}

export interface AvailableSlotsParams {
  serviceId: string;
  /** ISO date window (inclusive). booking-service reads `from`/`to` date params. */
  from?: string;
  to?: string;
}

/**
 * A concrete slot record on a service (booking-service `slots` module). Created
 * manually by a merchant to block out time (breaks, holidays, etc.) — distinct
 * from `AvailableSlot`, which is the read-only computed availability grid.
 */
export interface BookingSlot {
  id: string;
  serviceId: string;
  start: string;
  end: string;
  reason?: string | null;
  createdAt?: string;
}

export interface CreateSlotPayload {
  serviceId: string;
  start: string;
  end: string;
  reason?: string;
}

/**
 * Calls the osprey-api bookings routes (PE-1875). osprey-api scopes by the
 * admin's selected tenant via the `x-tenant-id` header, which `ApiFetch`
 * derives from `selectedTenantId` (see shared/utils/fetch.ts). The body is
 * forwarded as-is to booking-service, so it carries
 * tenantId/solutionId/chargingEnabled/etc.
 *
 * osprey-api exposes `@Post('services')` (create) and `@Patch('services/:id')`
 * (update) under `/bookings`. `ApiFetch.put` issues a PATCH on the wire (see
 * shared/utils/fetch.ts), so update maps to `PATCH /bookings/services/:id`.
 */
export const bookingServicesApi = {
  list: async (): Promise<BookingService[]> => {
    const response = await ApiFetch.get<Enveloped<BookingService[]>>('/bookings/services');
    return unwrap(response);
  },

  create: async (data: CreateBookingServicePayload): Promise<BookingService> => {
    const response = await ApiFetch.post<Enveloped<BookingService>>('/bookings/services', data);
    return unwrap(response);
  },

  update: async (id: string, data: UpdateBookingServicePayload): Promise<BookingService> => {
    // ApiFetch.put issues a PATCH (see shared/utils/fetch.ts) → PATCH /bookings/services/:id.
    const response = await ApiFetch.put<Enveloped<BookingService>>(`/bookings/services/${id}`, data);
    return unwrap(response);
  },

  // --- Booking rules / schedule settings (PE-2100) ---
  // osprey-api GET/PATCH /bookings/settings, scoped to the selected merchant via x-tenant-id.
  getSettings: async (): Promise<BookingSettings> => {
    const response = await ApiFetch.get<Enveloped<BookingSettings>>('/bookings/settings');
    return unwrap(response);
  },

  updateSettings: async (data: UpdateBookingSettingsPayload): Promise<BookingSettings> => {
    // PATCH is a partial upsert — only the supplied fields change.
    const response = await ApiFetch.put<Enveloped<BookingSettings>>('/bookings/settings', data);
    return unwrap(response);
  },

  // Read-only computed availability for a service over a date window (booking-service).
  availableSlots: async (params: AvailableSlotsParams): Promise<AvailableSlots> => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    const response = await ApiFetch.get<Enveloped<AvailableSlots>>(`/bookings/slots?${qs.toString()}`);
    return unwrap(response);
  },

  // --- Manual slot blocking (PE-2137) ---
  // osprey-api GET /bookings/services/:id/slots — the concrete slot records
  // (bookings + manual blocks) for one service, used to grey out buffer-affected
  // neighbours in the picker.
  listServiceSlots: async (serviceId: string): Promise<BookingSlot[]> => {
    const response = await ApiFetch.get<Enveloped<BookingSlot[]>>(`/bookings/services/${serviceId}/slots`);
    return unwrap(response);
  },

  // osprey-api POST /bookings/slots — creates a blocking slot (e.g. a merchant
  // break) on the given service.
  createSlot: async (data: CreateSlotPayload): Promise<BookingSlot> => {
    const response = await ApiFetch.post<Enveloped<BookingSlot>>('/bookings/slots', data);
    return unwrap(response);
  },

  // osprey-api DELETE /bookings/slots/:id — removes a manually-created block.
  deleteSlot: async (id: string): Promise<void> => {
    await ApiFetch.del(`/bookings/slots/${id}`);
  },
};
